import debounce from "lodash.debounce";

import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";

import {
    mergeEvents,
    multiply,
    type Event,
} from "./math-expressions-rendered-event.ts";

interface ConstructorParams {
    publish: (event: Event) => unknown;
    samplingFraction: number;
}

// TexAnalyticsService aggregates the analytics events that get emitted when
// math expressions are rendered, batching events that occur within a
// one-second window into a single event. This cuts down on network traffic. If
// we didn't have this class, we'd be making one HTTP request per math
// expression on the page.
export class TexAnalyticsService {
    _publish: (arg1: Event) => unknown;
    _samplingFraction: number;
    _queued: Record<string, Event> = {};
    constructor({publish, samplingFraction}: ConstructorParams) {
        this._publish = publish;
        this._samplingFraction = samplingFraction;
    }

    postEvent: (arg1: Event) => void = (event) => {
        this._queueEvent(event);
        this._sendQueuedEventsAfterTimeout();
    };

    _queueEvent(e: Event): void {
        const key = aggregationKey(e);
        this._queued[key] =
            this._queued[key] == null ? e : mergeEvents(this._queued[key], e);
    }

    _sendQueuedEvents: () => void = () => {
        Object.values(this._queued)
            // Randomly select `_samplingFraction` of the events, and discard
            // the rest.
            .filter(() => randomBoolean(this._samplingFraction))
            // Multiply the statistics in each sampled event by the inverse of
            // the sampling fraction, so the numbers we report to the analytics
            // service end up roughly accurate. E.g. if we sample 50% of the
            // events, we should double the numbers in each event.
            .map((event) => multiply(1 / this._samplingFraction, event))
            .forEach((event) => this._publish(event));
        this._queued = {};
    };

    _sendQueuedEventsAfterTimeout: () => unknown = debounce(
        this._sendQueuedEvents,
        1000,
    );
}

function aggregationKey(event: Event): string {
    return `${event.eventType}:${event.renderer}:${removeUrlHash(
        event.currentPageURL,
    )}`;
}

function removeUrlHash(url: string): string {
    return url.replace(/#.*/, "");
}

function randomBoolean(probabilityOfTrue: number): boolean {
    return Math.random() < probabilityOfTrue;
}

export const texAnalyticsService: TexAnalyticsService = new TexAnalyticsService(
    {
        publish: publishAnalyticsEventForMinorConversion,
        samplingFraction: 0.1,
    },
);
